import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type JSX, type ReactNode } from "react";

import { playCue } from "../lib/sound";
import { type MissionActionName, type UiMemoryRecord, type UiMissionView, type UiProposalReview, type UiTurn, type UiWorkspace, type VossErrorCode } from "../lib/types";
import { type VossClient } from "../lib/vossClient";
import { useTheme, type Theme } from "./useTheme";

export interface Message {
  readonly id: string;
  readonly role: "user" | "assistant";
  text: string;
  streaming: boolean;
  status?: string;
  turn?: UiTurn;
  missionId?: string;
  /** A grounded proposal awaiting the user's acceptance (M47 gate 1) — shown before a mission exists. */
  review?: UiProposalReview;
  /** The opaque server-side proposal id; the browser accepts/rejects by this alone. */
  proposalId?: string;
  /** Set once the proposal has been accepted or cancelled — the review card stops offering actions. */
  proposalClosed?: "accepted" | "cancelled";
  /** A typed terminal failure for this turn. Present ⇒ the turn did NOT complete; never a success. */
  failure?: { code: VossErrorCode; retryable: boolean; partial: boolean };
}
export interface Conversation {
  readonly id: string;
  sessionId: string | null;
  readonly workspaceId: string;
  title: string;
  messages: Message[];
}
export interface DebugEntry {
  readonly stage: string;
  readonly detail: unknown;
}

export interface VossContextValue {
  ready: boolean;
  fixtureMode: boolean;
  workspaces: readonly UiWorkspace[];
  currentWorkspaceId: string;
  currentWorkspace: UiWorkspace | null;
  selectWorkspace: (id: string) => void;
  conversations: readonly Conversation[];
  activeConversation: Conversation | null;
  selectConversation: (id: string) => void;
  newChat: () => void;
  sending: boolean;
  send: (message: string) => Promise<void>;
  /** Re-run the last user turn after a typed failure. */
  retry: () => Promise<void>;
  missions: Readonly<Record<string, UiMissionView>>;
  runMissionAction: (missionId: string, action: MissionActionName, payload?: { stepId?: string; checkpointId?: string; attemptId?: string; reason?: string }) => Promise<void>;
  /** Accept a pending proposal (M47 gate 1) by its opaque id → creates one mission, shown inline. */
  acceptProposal: (proposalId: string) => Promise<void>;
  /** Cancel a pending proposal by its opaque id. */
  rejectProposal: (proposalId: string) => Promise<void>;
  memory: readonly UiMemoryRecord[];
  debug: boolean;
  setDebug: (b: boolean) => void;
  debugLog: readonly DebugEntry[];
  theme: Theme;
  toggleTheme: () => void;
}

const VossContext = createContext<VossContextValue | null>(null);

const uid = (): string => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `id-${Math.floor(performance.now() * 1000)}`);
const titleOf = (message: string): string => (message.length > 42 ? `${message.slice(0, 42).trimEnd()}…` : message);
const newConversation = (workspaceId: string): Conversation => ({ id: uid(), sessionId: null, workspaceId, title: "New conversation", messages: [] });

export function VossProvider({ client, children }: { client: VossClient; children: ReactNode }): JSX.Element {
  const { theme, toggleTheme } = useTheme();
  const [ready, setReady] = useState(false);
  const [fixtureMode, setFixtureMode] = useState(false);
  const [workspaces, setWorkspaces] = useState<readonly UiWorkspace[]>([]);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState("ws.personal");
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [memory, setMemory] = useState<readonly UiMemoryRecord[]>([]);
  const [debug, setDebug] = useState(false);
  const [debugLog, setDebugLog] = useState<readonly DebugEntry[]>([]);
  const [sending, setSending] = useState(false);
  const [missions, setMissions] = useState<Record<string, UiMissionView>>({});
  const abortRef = useRef<AbortController | null>(null);

  // Bootstrap once.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const boot = await client.bootstrap();
      if (cancelled) return;
      setWorkspaces(boot.workspaces);
      setFixtureMode(boot.fixtureMode);
      const first = boot.workspaces[0]?.id ?? "ws.personal";
      setCurrentWorkspaceId(first);
      const conv = newConversation(first);
      setConversations([conv]);
      setActiveId(conv.id);
      setMemory(await client.memory(first));
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [client]);

  const patchMessage = useCallback((convId: string, msgId: string, patch: (m: Message) => Message) => {
    setConversations((prev) => prev.map((c) => (c.id !== convId ? c : { ...c, messages: c.messages.map((m) => (m.id === msgId ? patch(m) : m)) })));
  }, []);

  const selectWorkspace = useCallback(
    (id: string) => {
      setCurrentWorkspaceId(id);
      void client.memory(id).then(setMemory);
      setConversations((prev) => {
        const existing = prev.find((c) => c.workspaceId === id && c.messages.length === 0);
        if (existing) {
          setActiveId(existing.id);
          return prev;
        }
        const conv = newConversation(id);
        setActiveId(conv.id);
        return [conv, ...prev];
      });
    },
    [client],
  );

  const newChat = useCallback(() => {
    setConversations((prev) => {
      const conv = newConversation(currentWorkspaceId);
      setActiveId(conv.id);
      return [conv, ...prev];
    });
    setDebugLog([]);
  }, [currentWorkspaceId]);

  const send = useCallback(
    async (raw: string) => {
      const message = raw.trim();
      if (message.length === 0 || sending) return;
      const conv = conversations.find((c) => c.id === activeId);
      if (!conv) return;

      const sessionId = conv.sessionId ?? (await client.createSession(conv.workspaceId));
      const userMsg: Message = { id: uid(), role: "user", text: message, streaming: false };
      const assistantMsg: Message = { id: uid(), role: "assistant", text: "", streaming: true, status: "Understanding your request" };
      setConversations((prev) =>
        prev.map((c) => (c.id !== conv.id ? c : { ...c, sessionId, title: c.messages.length === 0 ? titleOf(message) : c.title, messages: [...c.messages, userMsg, assistantMsg] })),
      );
      setDebugLog([]);
      setSending(true);

      const controller = new AbortController();
      abortRef.current = controller;
      try {
        for await (const event of client.converse(sessionId, message, { debug }, controller.signal)) {
          if (event.type === "status") patchMessage(conv.id, assistantMsg.id, (m) => ({ ...m, status: event.label }));
          else if (event.type === "token") patchMessage(conv.id, assistantMsg.id, (m) => ({ ...m, text: m.text + event.text }));
          else if (event.type === "mission") {
            const view = event.view;
            setMissions((prev) => ({ ...prev, [view.missionId]: view }));
            patchMessage(conv.id, assistantMsg.id, (m) => ({ ...m, missionId: view.missionId }));
          } else if (event.type === "proposal") {
            // Gate 1: a grounded proposal to review. NOTHING is created yet — the browser holds only the
            // opaque id + sanitized review and accepts/rejects by id.
            const { review, proposalId } = event;
            patchMessage(conv.id, assistantMsg.id, (m) => ({ ...m, review, proposalId }));
          } else if (event.type === "debug") setDebugLog((prev) => [...prev, { stage: event.stage, detail: event.detail }]);
          else if (event.type === "error") {
            // Terminal typed failure from the host. The turn did NOT succeed — mark it failed and keep any
            // partial text visibly incomplete (never present a partial as a completed answer). No fabrication.
            const failure = { code: event.code, retryable: event.retryable, partial: event.partial };
            patchMessage(conv.id, assistantMsg.id, (m) => ({ ...m, streaming: false, status: undefined, failure }));
          } else if (event.type === "final") {
            patchMessage(conv.id, assistantMsg.id, (m) => ({ ...m, streaming: false, status: undefined, turn: event.turn, text: m.text.length > 0 ? m.text : event.turn.response }));
            playCue("receive");
          }
        }
      } catch {
        // A transport-level interruption (connection dropped before any terminal event). Not user-abort.
        if (!controller.signal.aborted) {
          patchMessage(conv.id, assistantMsg.id, (m) => ({ ...m, streaming: false, status: undefined, failure: { code: "UNKNOWN" as VossErrorCode, retryable: true, partial: m.text.length > 0 } }));
        } else {
          patchMessage(conv.id, assistantMsg.id, (m) => ({ ...m, streaming: false, status: undefined }));
        }
      } finally {
        setSending(false);
        abortRef.current = null;
      }
    },
    [activeId, client, conversations, debug, patchMessage, sending],
  );

  /** Re-send the last user message of the active conversation after a failed turn (Retry). */
  const retry = useCallback(async () => {
    const conv = conversations.find((c) => c.id === activeId);
    const lastUser = conv?.messages.filter((m) => m.role === "user").at(-1);
    if (lastUser) await send(lastUser.text);
  }, [activeId, conversations, send]);

  const runMissionAction = useCallback(
    async (missionId: string, action: MissionActionName, payload?: { stepId?: string; checkpointId?: string; attemptId?: string; reason?: string }) => {
      // Pass the current operating workspace (M49) so the server can enforce ownership; the wire carries
      // no mission-owner metadata, so the client supplies its trusted workspace context.
      const view = await client.missionAction(missionId, action, payload, currentWorkspaceId);
      setMissions((prev) => ({ ...prev, [missionId]: view }));
    },
    [client, currentWorkspaceId],
  );

  /** Locate the conversation + message that carries a given opaque proposal id. */
  const findProposal = useCallback(
    (proposalId: string): { conv: Conversation; msg: Message } | null => {
      for (const conv of conversations) {
        const msg = conv.messages.find((m) => m.proposalId === proposalId && !m.proposalClosed);
        if (msg && conv.sessionId) return { conv, msg };
      }
      return null;
    },
    [conversations],
  );

  const acceptProposal = useCallback(
    async (proposalId: string) => {
      const found = findProposal(proposalId);
      if (!found || !found.conv.sessionId) return;
      // Gate 1 → gate 2: the server resolves the opaque id to the trusted definition and creates one
      // mission. The browser never sends a definition/target/params — only the id + its session.
      const view = await client.acceptProposal(proposalId, found.conv.sessionId);
      setMissions((prev) => ({ ...prev, [view.missionId]: view }));
      patchMessage(found.conv.id, found.msg.id, (m) => ({ ...m, missionId: view.missionId, proposalClosed: "accepted" }));
    },
    [client, findProposal, patchMessage],
  );

  const rejectProposal = useCallback(
    async (proposalId: string) => {
      const found = findProposal(proposalId);
      if (!found || !found.conv.sessionId) return;
      await client.rejectProposal(proposalId, found.conv.sessionId);
      patchMessage(found.conv.id, found.msg.id, (m) => ({ ...m, proposalClosed: "cancelled" }));
    },
    [client, findProposal, patchMessage],
  );

  const value = useMemo<VossContextValue>(() => {
    const visible = conversations.filter((c) => c.workspaceId === currentWorkspaceId);
    return {
      ready,
      fixtureMode,
      workspaces,
      currentWorkspaceId,
      currentWorkspace: workspaces.find((w) => w.id === currentWorkspaceId) ?? null,
      selectWorkspace,
      conversations: visible,
      activeConversation: conversations.find((c) => c.id === activeId) ?? null,
      selectConversation: setActiveId,
      newChat,
      sending,
      send,
      retry,
      missions,
      runMissionAction,
      acceptProposal,
      rejectProposal,
      memory,
      debug,
      setDebug,
      debugLog,
      theme,
      toggleTheme,
    };
  }, [ready, fixtureMode, workspaces, currentWorkspaceId, selectWorkspace, conversations, activeId, newChat, sending, send, retry, missions, runMissionAction, acceptProposal, rejectProposal, memory, debug, debugLog, theme, toggleTheme]);

  return <VossContext.Provider value={value}>{children}</VossContext.Provider>;
}

export function useVoss(): VossContextValue {
  const ctx = useContext(VossContext);
  if (!ctx) throw new Error("useVoss must be used within a VossProvider");
  return ctx;
}
